import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import ModalLayout from './ModalLayout';
import Text from '@/components/Typography/Text';
import useResponsiveness from '@/hooks/useResponsiveness';
import PropTypes from 'prop-types';

// Create styles outside component
const createStyles = (isXS) => StyleSheet.create({
  content: {
    padding: isXS ? 15 : 15,
    paddingTop: isXS ? 5 : 5,
    paddingBottom: isXS ? 25 : 15,
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    flex: 1,
  },
  message: {
    textAlign: 'center',
    marginBottom: 5,
  },
  itemName: {
    textAlign: 'center',
    marginTop: 10,
    marginBottom: 5,
  },
});

// Memoized message component
const MessageContent = React.memo(({ message, itemName, styles }) => (
  <View style={styles.content}>
    {message && (
      <>
        {itemName && (
          <Text 
            style={styles.itemName}
            weight='medium'
            color='primary'
            accessibilityRole="text"
            accessibilityLabel={`Item: ${itemName}`}
          >
            {itemName}
          </Text>
        )}
        <Text 
          style={styles.message}
          color='secondary'
          accessibilityRole="text"
          accessibilityLabel={`Message: ${message}`}
        >
          {message}
        </Text>
      </>
    )}
  </View>
));

MessageContent.propTypes = {
  message: PropTypes.string.isRequired,
  itemName: PropTypes.string,
  styles: PropTypes.object.isRequired,
};

MessageContent.displayName = 'MessageContent';

const ConfirmationModal = ({
  title = 'Confirm',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDanger = false,
  itemName,
  onConfirm,
  onClose,
  onLowerActionsLayout,
}) => {
  const { isXS } = useResponsiveness();

  // Memoize styles
  const styles = useMemo(() => createStyles(isXS), [isXS]);

  // Memoize subtitle content
  const subtitleContent = useMemo(() => (
    <>
      {message}
      {itemName && (
        <Text 
          style={styles.itemName}
          accessibilityRole="text"
        >
          {itemName}
        </Text>
      )}
    </>
  ), [message, itemName, styles.itemName]);

  // Memoize actions
  const actions = useMemo(() => [
    {
      title: cancelText,
      primary: false,
      onPress: onClose,
      accessibilityLabel: `Cancel ${title.toLowerCase()}`,
    },
    {
      title: confirmText,
      primary: !isDanger,
      danger: isDanger,
      onPress: onConfirm,
      accessibilityLabel: `Confirm ${title.toLowerCase()}`,
    }
  ], [cancelText, confirmText, isDanger, onClose, onConfirm, title]);

  return (
    <ModalLayout
      onClose={onClose}
      onLowerActionsLayout={onLowerActionsLayout}
      title={title}
      maxHeight={300}
      subtitle={subtitleContent}
      hideCloseButton={true}
      titleBorder={false}
      inlineLowerActions={isXS ? false : true}
      lowerActionsBorder={false}
      lowerActions={actions}
      accessibilityLabel={`${title} confirmation dialog`}
      accessibilityRole="alertdialog"
    >
      <MessageContent
        message={message}
        itemName={itemName}
        styles={styles}
      />
    </ModalLayout>
  );
};

ConfirmationModal.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  isDanger: PropTypes.bool,
  itemName: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ConfirmationModal.displayName = 'ConfirmationModal';

// Define default modal options
ConfirmationModal.modalOptions = {
  width: 350,
  height: 250,
  heightMobile: 250,
};

export default ConfirmationModal;